import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Handshake, Loader2, Plus, KeyRound, Eye, Percent } from 'lucide-react';

const STATUS_COLORS = { active: 'default', pending: 'secondary', suspended: 'destructive', disabled: 'outline', rejected: 'destructive' };

export default function AdminResellersPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [resellers, setResellers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ business_name: '', contact_name: '', email: '', phone: '', company_name: '' });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loginFor, setLoginFor] = useState(null);
  const [pwd, setPwd] = useState('');
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([api.get('/admin/resellers'), api.get('/admin/reseller-settings')]);
      setResellers(r.data.resellers || []);
      setSettings(s.data || null);
    }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/reseller-settings', {
        min_withdrawal_usdt: parseFloat(settings.min_withdrawal_usdt) || 0,
        max_withdrawal_usdt: settings.max_withdrawal_usdt === '' || settings.max_withdrawal_usdt == null ? null : parseFloat(settings.max_withdrawal_usdt),
        withdrawal_fee_percentage: parseFloat(settings.withdrawal_fee_percentage) || 0,
        withdrawal_fee_fixed_usdt: parseFloat(settings.withdrawal_fee_fixed_usdt) || 0,
      });
      toast.success('Withdrawal settings saved');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingSettings(false); }
  };

  const create = async () => {
    if (!form.business_name || !form.email) { toast.error('Business name and email required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/resellers', { ...form, status: 'pending' });
      toast.success('Reseller created'); setCreateOpen(false);
      setForm({ business_name: '', contact_name: '', email: '', phone: '', company_name: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/resellers/${id}/status`, { status }); toast.success(`Reseller ${status}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const createLogin = async () => {
    if (pwd.length < 6) { toast.error('Password must be 6+ chars'); return; }
    try { const r = await api.post(`/admin/resellers/${loginFor.id}/create-login`, { password: pwd });
      toast.success(`Login created: ${r.data.email}`); setLoginFor(null); setPwd(''); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openDetail = async (id) => {
    try { const r = await api.get(`/admin/resellers/${id}`); setDetail(r.data); } catch { toast.error('Failed to load'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-resellers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Handshake className="w-6 h-6" />Resellers</h1>
          <p className="text-muted-foreground">Manage partner resellers, approvals and logins.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-reseller-btn"><Plus className="w-4 h-4 mr-2" />Create Reseller</Button>
      </div>

      {/* Withdrawal settings */}
      {settings && (
        <Card data-testid="withdrawal-settings-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Percent className="w-4 h-4" />Withdrawal Settings</CardTitle>
            <CardDescription>Configure the minimum withdrawal and an optional fee to recover on-chain transfer costs.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div><Label className="text-xs">Min Withdrawal (USDT)</Label>
                <Input type="number" value={settings.min_withdrawal_usdt ?? 100} onChange={(e) => setSettings({ ...settings, min_withdrawal_usdt: e.target.value })} className="rounded-sm" data-testid="set-min-withdrawal" /></div>
              <div><Label className="text-xs">Max Withdrawal (USDT, optional)</Label>
                <Input type="number" value={settings.max_withdrawal_usdt ?? ''} onChange={(e) => setSettings({ ...settings, max_withdrawal_usdt: e.target.value })} placeholder="No limit" className="rounded-sm" data-testid="set-max-withdrawal" /></div>
              <div><Label className="text-xs">Withdrawal Fee (%)</Label>
                <Input type="number" value={settings.withdrawal_fee_percentage ?? 0} onChange={(e) => setSettings({ ...settings, withdrawal_fee_percentage: e.target.value })} className="rounded-sm" data-testid="set-fee-pct" /></div>
              <div><Label className="text-xs">Fixed Fee (USDT)</Label>
                <Input type="number" value={settings.withdrawal_fee_fixed_usdt ?? 0} onChange={(e) => setSettings({ ...settings, withdrawal_fee_fixed_usdt: e.target.value })} className="rounded-sm" data-testid="set-fee-fixed" /></div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-muted-foreground">Base platform fee: {settings.awadhpay_base_percentage}% + ₹{settings.awadhpay_base_fixed} · USDT rate set under USDT Funding.</p>
              <Button size="sm" onClick={saveSettings} disabled={savingSettings} data-testid="save-withdrawal-settings">{savingSettings && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Save Settings</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr><th className="px-4 py-3">Reseller</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Merchants</th><th className="px-4 py-3">Commission</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {resellers.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No resellers yet.</td></tr>}
              {resellers.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`reseller-row-${r.reseller_code}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.business_name}</div>
                    <div className="text-xs text-muted-foreground">{r.email}{r.user_id ? '' : ' · no login'}</div>
                  </td>
                  <td className="px-4 py-3 font-mono">{r.reseller_code}</td>
                  <td className="px-4 py-3">{r.merchant_count}</td>
                  <td className="px-4 py-3 font-mono">₹{(r.total_commission || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3"><Badge variant={STATUS_COLORS[r.status] || 'secondary'}>{r.status}</Badge></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end flex-wrap">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(r.id)} data-testid={`view-${r.reseller_code}`}><Eye className="w-4 h-4" /></Button>
                      {(r.status === 'pending' || r.status === 'suspended' || r.status === 'disabled') && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(r.id, 'active')} data-testid={`approve-${r.reseller_code}`}>Approve</Button>)}
                      {r.status === 'active' && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(r.id, 'suspended')} data-testid={`suspend-${r.reseller_code}`}>Suspend</Button>)}
                      {r.status === 'pending' && (
                        <Button size="sm" variant="ghost" onClick={() => setStatus(r.id, 'rejected')}>Reject</Button>)}
                      {!r.user_id && (
                        <Button size="sm" variant="ghost" onClick={() => setLoginFor(r)} data-testid={`login-${r.reseller_code}`}><KeyRound className="w-4 h-4" /></Button>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Reseller</DialogTitle><DialogDescription>A unique reseller code is generated automatically.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {['business_name', 'contact_name', 'email', 'phone', 'company_name'].map((f) => (
              <div key={f}><Label className="capitalize">{f.replace('_', ' ')}</Label>
                <Input value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`create-${f}`} /></div>
            ))}
          </div>
          <DialogFooter><Button onClick={create} disabled={saving} data-testid="submit-create-reseller">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create login dialog */}
      <Dialog open={!!loginFor} onOpenChange={(o) => !o && setLoginFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Reseller Login</DialogTitle><DialogDescription>Login email: {loginFor?.email}</DialogDescription></DialogHeader>
          <div><Label>Temporary Password</Label><Input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reseller-login-password" /></div>
          <DialogFooter><Button onClick={createLogin} data-testid="submit-create-login">Create Login</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.reseller?.business_name} ({detail?.reseller?.reseller_code})</DialogTitle>
            <DialogDescription>Status: {detail?.reseller?.status} · {detail?.merchants?.length || 0} merchants</DialogDescription></DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground"><tr><th className="px-3 py-2">Merchant</th><th className="px-3 py-2">KYC</th><th className="px-3 py-2">Pricing</th></tr></thead>
              <tbody>
                {(detail?.merchants || []).length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">No merchants assigned.</td></tr>}
                {(detail?.merchants || []).map((m) => (
                  <tr key={m.merchant.id} className="border-t"><td className="px-3 py-2">{m.merchant.business_name}</td>
                    <td className="px-3 py-2">{m.merchant.kyc_status}</td>
                    <td className="px-3 py-2 font-mono">{m.pricing ? `${m.pricing.merchant_percentage_fee}% + ₹${m.pricing.merchant_fixed_fee}` : 'base'}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
